import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {
  enums,
  get,
  parseJSONOrDefault,
  timeDurationIsValid,
} from '../../../utils';
import { isNonWorkDay } from '../shared-utils';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

type IValidator = (v: any, vm: string, params: any[]) => string;

export const defaultValidationMessages = {
  hasContent: enums.ValidationErrors.hasContent,
  signerHasContent: enums.ValidationErrors.signerHasContent,
  weeklyScheduleHasContent: enums.ValidationErrors.weeklyScheduleHasContent,
  weeklyScheduleAllZeros: enums.ValidationErrors.weeklyScheduleAllZeros,
  noAlpha: enums.ValidationErrors.noAlpha,
  matchesPattern: enums.ValidationErrors.matchesPattern,
  dateGreaterThan: enums.ValidationErrors.dateGreaterThan,
  dateLesserThan: enums.ValidationErrors.dateLesserThan,
  dateGreaterThanOrEqual: enums.ValidationErrors.dateGreaterThanOrEqual,
  dateLesserThanOrEqual: enums.ValidationErrors.dateLesserThanOrEqual,
  dateWorkingDay: enums.ValidationErrors.dateWorkingDay,
  isValidDateSelection: enums.ValidationErrors.isValidDate,
  noSpecialChars: enums.ValidationErrors.noSpecialChars,
  durationGreaterThan: enums.ValidationErrors.durationGreaterThan,
  durationLesserThan: enums.ValidationErrors.durationLesserThan,
  durationGreaterThanOrEqual: enums.ValidationErrors.durationGreaterThanOrEqual,
  durationLesserThanOrEqual: enums.ValidationErrors.durationLesserThanOrEqual,
};

export const isValidDateSelection: IValidator = (
  v,
  validationMessage,
  params,
) => {
  if (v == null && params[0]?.required !== true) {
    return '';
  }

  const d = dayjs(v).format('YYYY-MM-DD');

  const re = /([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
  const r = re.test(d);

  return r ? '' : validationMessage;
};

const hasContentFieldTypeMessageMap = {
  Signer: defaultValidationMessages.signerHasContent,
};

export const signerHasContent: IValidator = (v, validationMessage, _params) => {
  const returnMessage =
    validationMessage === defaultValidationMessages.hasContent
      ? defaultValidationMessages.signerHasContent
      : validationMessage;

  const isSigned = get(v, 'signed', false);
  const hasInsuredsName = get(v, 'insuredsName', '').length > 0;
  const hasSignedDate = get(v, 'signedDate', '').length > 0;

  const onBehalfSelected = get(v, 'onBehalfSelected', false);
  const hasSignersName = onBehalfSelected
    ? get(v, 'signersName', '').length > 0
    : true;
  const hasSignersRelationship = onBehalfSelected
    ? get(v, 'signersRelationship', '').length > 0
    : true;

  const baseFieldsFilled = isSigned && hasInsuredsName && hasSignedDate;
  const behalfOfFilled = onBehalfSelected
    ? hasSignersName && hasSignersRelationship
    : true;

  return baseFieldsFilled && behalfOfFilled ? '' : returnMessage;
};

/**
 * A schedule day is considered "zero" when it is empty or resolves to 00:00,
 * i.e. no hours/minutes have been entered for that day.
 */
const isZeroDuration = (value: string): boolean => {
  const [hours, minutes] = (value ?? '').split(':').map(Number);
  return (
    (Number.isNaN(hours) ? 0 : hours) === 0 &&
    (Number.isNaN(minutes) ? 0 : minutes) === 0
  );
};

export const weeklyScheduleHasContent: IValidator = (
  v,
  validationMessage,
  _params,
) => {
  const isDefaultMessage =
    validationMessage === defaultValidationMessages.hasContent;

  const invalidFormatMessage = isDefaultMessage
    ? defaultValidationMessages.weeklyScheduleHasContent
    : validationMessage;

  if ((v?.weeks && !Array.isArray(v.weeks)) || !Array.isArray(v.weeks[0])) {
    return invalidFormatMessage;
  }

  const weeksInvalid = v.weeks.some((w: string[]) =>
    w.some((cv) => !timeDurationIsValid(cv)),
  );

  if (weeksInvalid) {
    return invalidFormatMessage;
  }

  // Weeks the user explicitly marked as non-working are exempt from the
  // "must have hours" rule; every other week must have at least one time value.
  const nonWorkingWeeks: boolean[] = Array.isArray(v.nonWorkingWeeks)
    ? v.nonWorkingWeeks
    : [];

  const hasEmptyWorkingWeek = v.weeks.some(
    (w: string[], i: number) => !nonWorkingWeeks[i] && w.every(isZeroDuration),
  );

  if (hasEmptyWorkingWeek) {
    return isDefaultMessage
      ? defaultValidationMessages.weeklyScheduleAllZeros
      : validationMessage;
  }

  return '';
};

const validatePrimitiveTypes = (value: any, returnMessage: string) => {
  if (typeof value === 'string') {
    return value?.length > 0 ? '' : returnMessage;
  } else if (value?.toISOString && !isNaN(value.getTime())) {
    return value.toISOString() === new Date(0).toISOString()
      ? returnMessage
      : '';
  } else if (typeof value === 'boolean') {
    return value ? '' : returnMessage;
  }

  // if we get this far without returning, check if we can parse as JSON
  try {
    return Object.keys(parseJSONOrDefault(JSON.stringify(value))).length === 0
      ? returnMessage
      : '';
  } catch (e) {
    return returnMessage;
  }
};

export const hasContent: IValidator = (value, validationMessage, params) => {
  const formFieldType: string = params[0];
  const defaultMessage = get(
    hasContentFieldTypeMessageMap,
    formFieldType,
    defaultValidationMessages.hasContent,
  );
  const returnMessage = validationMessage || defaultMessage;

  // handle undefinedish
  if (value === undefined || value === null) {
    return returnMessage;
  }

  // handle custom objects
  if (formFieldType === enums.FormFieldTypes.signer) {
    return signerHasContent(value, returnMessage, []);
  } else if (
    formFieldType === enums.FormFieldTypes.weeklyschedule ||
    formFieldType === enums.FormFieldTypes.reducedschedulehours
  ) {
    return weeklyScheduleHasContent(value, returnMessage, []);
  }

  //Handle arrays
  if (Array.isArray(value)) {
    return value.length > 0 ? '' : returnMessage;
  }

  // handle primitives and others
  return validatePrimitiveTypes(value, returnMessage);
};

export const matchesPattern = (
  value: any,
  validationMessage = defaultValidationMessages.matchesPattern,
  params: any[] = [],
) => {
  const re = new RegExp(params[0], 'i');
  const r = re.test(value?.toString());

  return r ? '' : validationMessage;
};

export const isAValidDate = (value: any, validationMessage: string) => {
  const d = new Date(value);
  const validDate = d instanceof Date && !isNaN(d.valueOf());
  return validDate ? '' : validationMessage;
};

export const timeGreaterThan = (
  value: any,
  validationMessage: string,
  params: any[] = [],
) => {
  const date1 = new Date(value);
  const date2 = new Date(params[0]);
  const greater = date1.getTime() > date2.getTime();
  return greater ? '' : validationMessage;
};

export const noAlpha = (
  value: string,
  validationMessage = defaultValidationMessages.noAlpha,
  _params?: any[],
) => (!value || !/[a-zA-Z]/g.test(value) ? '' : validationMessage);

export const noSpecialChars = (
  value: string,
  validationMessage = defaultValidationMessages.noSpecialChars,
  _params?: any[],
) => (!value || !/[="><]/g.test(value) ? '' : validationMessage);

// duration validators (HH:mm format)
const parseDurationMinutes = (value: string): number => {
  const [hours, minutes] = (value ?? '').split(':').map(Number);
  return (
    (Number.isNaN(hours) ? 0 : hours) * 60 +
    (Number.isNaN(minutes) ? 0 : minutes)
  );
};

export const durationGreaterThan: IValidator = (value, validationMessage, p) =>
  parseDurationMinutes(value) > parseDurationMinutes(p[0])
    ? ''
    : validationMessage;
export const durationLesserThan: IValidator = (value, validationMessage, p) =>
  parseDurationMinutes(value) < parseDurationMinutes(p[0])
    ? ''
    : validationMessage;
export const durationGreaterThanOrEqual: IValidator = (
  value,
  validationMessage,
  p,
) =>
  parseDurationMinutes(value) >= parseDurationMinutes(p[0])
    ? ''
    : validationMessage;
export const durationLesserThanOrEqual: IValidator = (
  value,
  validationMessage,
  p,
) =>
  parseDurationMinutes(value) <= parseDurationMinutes(p[0])
    ? ''
    : validationMessage;

// date validators
export const dateGreaterThan: IValidator = (value, validationMessage, p) =>
  dayjs(value).isAfter(dayjs(p[0]), 'date') ? '' : validationMessage;
export const dateLesserThan: IValidator = (value, validationMessage, p) =>
  dayjs(value).isBefore(dayjs(p[0]), 'date') ? '' : validationMessage;
export const dateGreaterThanOrEqual: IValidator = (
  value,
  validationMessage,
  p,
) => (dayjs(value).isSameOrAfter(dayjs(p[0]), 'date') ? '' : validationMessage);
export const dateLesserThanOrEqual: IValidator = (
  value,
  validationMessage,
  p,
) => {
  return dayjs(value).isSameOrBefore(dayjs(p[0]), 'date')
    ? ''
    : validationMessage;
};
export const dateBeforeClaimStartDate: IValidator = (
  value,
  validationMessage,
  p,
) => {
  validationMessage = `Please note that the date of the absence cannot be before the start date of the claim (${dayjs(
    p[0],
  ).format('MM/DD/YYYY')}). Kindly adjust the date accordingly.`;
  return dayjs(value).isSameOrAfter(dayjs(p[0]), 'date')
    ? ''
    : validationMessage;
};
export const dateAfterClaimEndDate: IValidator = (
  value,
  validationMessage,
  p,
) => {
  validationMessage = `Please note that the date of the absence cannot be after the end date of the claim (${dayjs(
    p[0],
  ).format('MM/DD/YYYY')}). Kindly adjust the date accordingly.`;
  return dayjs(value).isSameOrBefore(dayjs(p[0]), 'date')
    ? ''
    : validationMessage;
};

export const dateValidWorkDay: IValidator = (value, validationMessage, p) => {
  const message = validationMessage || defaultValidationMessages.dateWorkingDay;
  const schedule = p[0] ?? p[1];
  const overrideValue = p[2];

  if (overrideValue === 'Yes') {
    return '';
  }

  if (!schedule) {
    return '';
  }

  return isNonWorkDay(value, schedule)
    ? message.replace('{date}', dayjs(value).format('MM/DD/YYYY'))
    : '';
};

export const customValidatorsMap: {
  [key: string]: any;
} = {
  hasContent,
  matchesPattern,
  noAlpha,
  dateGreaterThan,
  dateLesserThan,
  dateGreaterThanOrEqual,
  dateLesserThanOrEqual,
  isAValidDate,
  timeGreaterThan,
  noSpecialChars,
  dateBeforeClaimStartDate,
  dateAfterClaimEndDate,
  durationGreaterThan,
  durationLesserThan,
  durationGreaterThanOrEqual,
  durationLesserThanOrEqual,
  dateValidWorkDay,
};

export const handleValidation = (
  optionsTemplate: { [key: string]: any },
  isRequired: boolean,
  value: any,
  formFieldType: string | undefined,
) => {
  let returnMessage = '';
  const customValidators = optionsTemplate.validators || [];

  const validationMessages = {
    ...defaultValidationMessages,
    ...(optionsTemplate.validationMessages ?? {}),
  };

  if (isRequired) {
    returnMessage = hasContent(value, validationMessages.hasContent, [
      formFieldType,
      8,
    ]);
  }

  if (returnMessage.length === 0 && formFieldType === 'DatePicker') {
    returnMessage = isValidDateSelection(
      value,
      validationMessages.isValidDateSelection,
      [{ required: isRequired }],
    );
  }

  // handle custom validators
  if (returnMessage.length === 0 && customValidators.length > 0) {
    returnMessage = getMessageForCustomValidators(
      customValidators,
      value,
      validationMessages,
    );
  }

  return returnMessage;
};

const getMessageForCustomValidators = (
  customValidators: any[],
  value: any,
  validationMessages: { [key: string]: string },
) => {
  let returnMessage = '';
  // validators clone
  const vc: any[] = JSON.parse(JSON.stringify(customValidators));

  // shift through validators
  while (vc.length > 0 && returnMessage.length === 0) {
    const vKey = vc.shift();
    let v: IValidator;
    let params: any[] = [];
    let validationMessage = '';

    if (typeof vKey !== 'string') {
      // assume it's an object that matches expectations
      v = customValidatorsMap[vKey.name];
      params = vKey.params;
      validationMessage = validationMessages[vKey.name];
      if (vKey.property) {
        value = value[vKey.property];
      }
    } else {
      v = customValidatorsMap[vKey];
      validationMessage = validationMessages[vKey];
    }

    // execute validator
    returnMessage = v(value?.toString(), validationMessage, params) ?? '';
  }

  return returnMessage;
};
